// Response shape of POST /api/analytics, shared by the route (producer) and
// the /analytics client components (consumer, type-only imports).

// The time window a request covers: either the trailing N days (relative to
// "now" in the PostHog project timezone — what a window ending today sends)
// or an explicit inclusive start/end calendar-date pair from the scrubber.
// Date strings are strictly YYYY-MM-DD; the server re-validates the format
// before they're ever interpolated into HogQL.
export type AnalyticsRange =
  | { kind: 'days'; days: number }
  | { kind: 'custom'; start: string; end: string }

// Upper bound on a range's span, shared by the server's request validation
// and the client's date-input guard. Deliberately far beyond any site's
// current life so the scrubber can select a site's ENTIRE history (its real
// ceiling is the site's launch→today span); this cap only bounds the daily
// query's row count and the gap-fill loop against a hand-crafted request.
export const MAX_ANALYTICS_RANGE_DAYS = 3660

// Chunk span of the timeline scrubber's full-history sweep. Kept at a year
// per chunk (NOT MAX_ANALYTICS_RANGE_DAYS) so each chunk stays one cheap
// query and the readership graph grows leftward progressively.
export const HISTORY_CHUNK_DAYS = 366

// Strict YYYY-MM-DD check. The regex pins the shape; Date.parse rejects
// impossible dates the regex lets through (2026-02-31 → NaN, since the ISO
// date-only parser refuses out-of-range days).
const ANALYTICS_DATE_RE = /^\d{4}-\d{2}-\d{2}$/
export const isAnalyticsDate = (value: unknown): value is string =>
  typeof value === 'string' &&
  ANALYTICS_DATE_RE.test(value) &&
  !Number.isNaN(Date.parse(value))

// "Today" as the user's local calendar date — the fixed right edge of the
// timeline scrubber AND the end bound of the dashboard's full-history sweep,
// so both agree on where the site's life ends. Matches the trailing-days
// presets (which end now).
export const localTodayIso = () => {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

// Number of calendar days a range spans (inclusive on both ends), used for
// the "pageviews / day" card and the server's gap-fill/limit math.
export const rangeDayCount = (range: AnalyticsRange): number =>
  range.kind === 'days'
    ? range.days
    : Math.round(
        (Date.parse(range.end) - Date.parse(range.start)) / 86_400_000,
      ) + 1

// Canonical string form of a range — the client's stale-response guard and
// URL/state comparisons both key on it, so "same range" has one definition.
export const analyticsRangeKey = (range: AnalyticsRange): string =>
  range.kind === 'days' ? `d${range.days}` : `${range.start}_${range.end}`

// The dashboard shows the same panels for each of our websites, one per tab.
// Every site is a distinct PostHog project, so each carries its own env-var
// prefix — the server reads `${envPrefix}_PERSONAL_API_KEY` / `_HOST` /
// `_PROJECT_ID` for whichever site the request names. `ai-2040` keeps the
// original un-suffixed POSTHOG_ANALYTICS_* vars so the existing deploy is
// untouched; `ai-2027` reads POSTHOG_ANALYTICS_2027_*.
// `launched` is the site's first public day — the left edge of the timeline
// scrubber (no PostHog data can predate it, so the timeline never offers
// dates with nothing behind them).
// `hasPlanChoices` marks the site whose front page is the choose-your-path
// playbook (plan selections tracked in the ?choices= URL param) — it gates
// the plan-exits query and panel, which are meaningless for other sites.
export const ANALYTICS_SITES = [
  {
    id: 'ai-2040',
    label: 'AI 2040',
    envPrefix: 'POSTHOG_ANALYTICS',
    launched: '2026-04-20',
    hasPlanChoices: true,
  },
  {
    id: 'ai-2027',
    label: 'AI 2027',
    envPrefix: 'POSTHOG_ANALYTICS_2027',
    launched: '2025-04-03',
    hasPlanChoices: false,
  },
] as const

export type AnalyticsSite = (typeof ANALYTICS_SITES)[number]
export type AnalyticsSiteId = AnalyticsSite['id']

export const DEFAULT_ANALYTICS_SITE_ID: AnalyticsSiteId = 'ai-2040'

export const getAnalyticsSite = (id: unknown): AnalyticsSite | null =>
  ANALYTICS_SITES.find(site => site.id === id) ?? null

export type DailyTrafficPoint = {
  day: string // YYYY-MM-DD, bucketed in the PostHog project timezone
  pageviews: number
  visitors: number
}

// One day of the full-history readership graph behind the timeline scrubber.
// Deliberately just the pageview count — no uniq(person_id) visitor column —
// so the history queries stay cheap enough to sweep the site's entire life.
export type HistoryDailyPoint = {
  day: string // YYYY-MM-DD, bucketed in the PostHog project timezone
  pageviews: number
}

// The `history` tier's payload: daily pageview counts for the requested
// window. NOT part of AnalyticsSummary — history is range-independent (the
// client always sweeps launch → today in chunks after the initial load) and
// is cached separately client-side so range changes don't wipe or refetch it.
export type AnalyticsHistorySummary = {
  historyDaily: HistoryDailyPoint[]
}

// One row of a grouped breakdown (top pages, referrers, devices, hovers).
// `count` is pageviews for the $pageview panels and hover count for the
// hovered-elements panel; each panel labels the column itself.
//
// The engagement fields (duration + scroll) are only populated for the
// pageview-based panels (pages / referrers / devices), where "how long the
// session lasted" and "how far down the page they scrolled" are meaningful.
// The interaction panels (hovers, HUD clicks) leave them undefined, and the
// table hides the engagement columns for any panel whose rows lack them.
export type BreakdownRow = {
  label: string
  count: number
  visitors: number
  // Mean session duration (seconds) across this row's pageviews. Averaged per
  // pageview, not per session, so a row is weighted by how many pageviews it
  // drew — i.e. "when someone was here, how long was their whole visit".
  avgDurationSeconds?: number
  // Distribution of those session durations across DURATION_BUCKETS (same
  // order/length), for the hover histogram. Undefined when no duration data.
  durationHistogram?: number[]
  // Mean of each pageview's max scroll depth (0–100%) for this row, sourced
  // from $pageleave's $prev_pageview_max_scroll_percentage.
  avgScrollPercent?: number
  // Distribution of those scroll depths across SCROLL_BUCKETS (same
  // order/length), for the hover histogram. Undefined when no scroll data.
  scrollHistogram?: number[]
}

// Histogram bucket definitions, shared by the HogQL query that fills the
// `*Histogram` arrays (bucket i counts values in [lo, hi)) and the table that
// renders them, so the bars and their labels can never drift out of sync.
// `label` is the axis tick shown under each bar in the tooltip.
export type HistogramBucket = { label: string }

// Session-duration buckets. Log-ish spacing because durations span seconds to
// tens of minutes; a linear scale would pile everything into one bar. The last
// bucket is open-ended (30m+). Order/length must match the SQL CASE below.
export const DURATION_BUCKETS: HistogramBucket[] = [
  { label: '<10s' },
  { label: '10s' },
  { label: '30s' },
  { label: '1m' },
  { label: '3m' },
  { label: '10m' },
  { label: '30m+' },
]

// Scroll-depth buckets, as a fraction of the page reached. Finer near the top
// (where bounces cluster) and a dedicated 95–100% "read to the end" bucket.
export const SCROLL_BUCKETS: HistogramBucket[] = [
  { label: '<10%' },
  { label: '25%' },
  { label: '50%' },
  { label: '75%' },
  { label: '95%' },
  { label: '100%' },
]

// The summary is split into tiers so every panel renders the moment its own
// data is ready instead of waiting for the slowest query in a shared batch:
//
//   core         = the summary cards + daily chart. Two fast queries.
//   tables       = the three pageview breakdowns (pages / referrers /
//                  devices) as plain count/visitor rows — cheap GROUP BYs
//                  with no session join, ~2× faster than the full queries —
//                  so the tables paint early with their base columns.
//   interactions = the hovers + HUD-clicks panels plus their shared
//                  comparison-visitors denominator. Shipped together because
//                  the two panels' % columns must divide by the same number.
//   engagement   = the same three pageview breakdowns re-delivered WITH the
//                  expensive columns (session-duration join + histograms,
//                  scroll depth) — the slowest queries, folded into the
//                  already-rendered tables when they land. Also carries the
//                  plan-exits panel for sites with hasPlanChoices.
//
// AnalyticsSummary is the union, so consumers that take the whole thing (the
// `rest` and `full` tiers) are unchanged.
export type AnalyticsCoreSummary = {
  totals: { pageviews: number; visitors: number }
  daily: DailyTrafficPoint[]
}

// The three pageview breakdowns. The `tables` tier delivers them as plain
// count/visitor rows; the `engagement` tier delivers the SAME keys with the
// duration/scroll fields populated — same shape, richer rows, so the client
// can simply replace one with the other.
export type AnalyticsTablesSummary = {
  topPages: BreakdownRow[]
  referrers: BreakdownRow[]
  devices: BreakdownRow[]
  // Front-page ("/") exits grouped by which plan the exit URL's ?choices=
  // param named — i.e. which plan was open when the visitor left. Only the
  // engagement tier delivers it, and only for sites with hasPlanChoices (the
  // key is omitted entirely for the rest, which is what hides the panel).
  // This split exists because the front page's scrollable length changes
  // several-fold as plans open, so the blended "/" scroll average mixes
  // incomparable denominators; see fetchEngagementSummary for why the exit
  // URL matches the scroll measurement's page state exactly.
  planExits?: BreakdownRow[]
  // Page-exits dropped from planExits because their non-empty ?choices= value
  // matched no plan root id — URLs mangled in transit (privacy proxies
  // truncating or letter-scrambling query params). Some plan was open for
  // these, but not reliably the one the string resembles, so rather than
  // misattribute them the panel excludes them and discloses this count in its
  // footnote. Delivered alongside planExits (same tiers, same sites).
  planExitsExcluded?: number
}

export type AnalyticsInteractionsSummary = {
  // Distinct visitors in the "comparison window": since hover tracking started
  // capturing, clamped into the selected range. The interaction panels (hovers
  // AND HUD clicks) both count over this window and divide by this number, so
  // their "% of visitors" columns are directly comparable — a hover % and a
  // click % are fractions of the same people. Without it, a range extending
  // before hover tracking shipped would deflate hover percentages against
  // visitors who never had the chance to hover, while clicks used the full
  // range — making the two panels incomparable. Null when no hover events exist
  // yet (nothing to divide by; panels fall back to the range total).
  comparisonVisitors: number | null
  hovers: BreakdownRow[]
  hudClicks: BreakdownRow[]
}

// Everything below the fold in one piece — what the legacy `rest` tier returns.
export type AnalyticsRestSummary = AnalyticsTablesSummary &
  AnalyticsInteractionsSummary

export type AnalyticsSummary = AnalyticsCoreSummary & AnalyticsRestSummary

// Which slice of the summary a request wants.
//   verify       — no data at all; just confirms the password (returns the
//                  moment auth passes, runs zero PostHog queries). The client
//                  uses it to open the dashboard immediately, then streams
//                  every panel in.
//   core         — summary cards + daily chart.
//   tables       — the pageview breakdowns, base columns only.
//   interactions — hovers + HUD clicks + the comparison denominator.
//   engagement   — the pageview breakdowns enriched with duration/scroll.
//   history      — daily pageview counts only, for the timeline scrubber's
//                  readership graph. The client sweeps the site's whole life
//                  as a series of these after the initial load settles.
//   rest         — interactions + engagement in one response (legacy shape).
//   full         — core + rest in one shot; the default for direct/legacy
//                  callers and the background prefetch of the not-yet-viewed
//                  site.
export const ANALYTICS_TIERS = [
  'verify',
  'core',
  'tables',
  'interactions',
  'engagement',
  'history',
  'rest',
  'full',
] as const
export type AnalyticsTier = (typeof ANALYTICS_TIERS)[number]

// Response envelope. `posthog` holds only the tier that was requested, tagged
// by `tier` so the client knows what it got and can merge core+rest. A `full`
// or `core` response always carries the core fields; a `rest` response carries
// only the rest fields; a `history` response carries only historyDaily. Typing
// it as a partial of everything keeps the envelope simple while the client
// narrows on `tier`.
export type AnalyticsResponse = {
  site: AnalyticsSiteId
  range: AnalyticsRange
  tier: AnalyticsTier
  posthog: Partial<AnalyticsSummary & AnalyticsHistorySummary> | null
  // Set when PostHog couldn't be queried (personal API key missing, upstream
  // error). The page renders this as a setup notice, not a failure.
  posthogError?: string
}
